import { Box, Checkbox, FormControlLabel, type SxProps, type Theme } from "@mui/material";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import InHouseBarChart from "../../../components/UIToolkit/Graphs/BarChart/InHouseBarChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobColors } from "../../../hooks/useJobColors";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { INK } from "../../../theme/tokens";
import { roleColor as automaticRoleColor, type RoleColorResolver } from "../../../utils/categoricalColors";
import { NurseFullRole } from "../../../utils/models";
import { getShortRole } from "../../../utils/utilFunctions";
import { useGetManagementCenterShiftPickUps } from "../hooks";
import { buildClampRange, clampDateToRange, detectGroupingMode, fetchedDataToBarDataGroups, generateAllPeriodDateTimes } from "../utils";

// INH-3407: BE emits this sentinel for shifts with no role/job assignment so the FE can
// render a translated label (t("unknown")) instead of an English string. Matches
// `_UNKNOWN_GROUP_KEY` in backend/services/management_center.py.
const UNKNOWN_KEY = "UNKNOWN";

const stringToFullRoleToColor = (role: string, roleColor: RoleColorResolver) => {
	if (Object.values(NurseFullRole).includes(role as NurseFullRole)) {
		return roleColor(role as NurseFullRole).dot;
	}
};

export const ShiftPickups = ({
	dateFrom,
	dateTo,
	roles,
	jobs,
	groupBy,
	onGroupByChange,
	shiftHours,
	excludeHolidays,
	excludeAgency,
	showAgencyPickups,
	onShowAgencyPickupsToggle,
	trailing,
	containerSx,
	shrinkLegendForSmallScreens,
	departmentId,
}: {
	dateFrom: DateTime;
	dateTo?: DateTime;
	roles?: string[];
	jobs?: string[];
	/** INH-3407: defaults to "role"; switches to "job" via the in-header toggle when the flag is on. */
	groupBy?: "role" | "job";
	onGroupByChange?: (mode: "role" | "job") => void;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	excludeAgency?: boolean;
	showAgencyPickups?: boolean;
	onShowAgencyPickupsToggle?: (enabled: boolean) => void;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	departmentId?: string;
}) => {
	const personalRoleColor = useRoleColors();
	const { t } = useTranslation();
	const { trackError } = useAnalytics();
	const { timezone, chosenDepartment } = useHospitalData();
	// DEFAULT: An Ops drill-down can show another department, whose personal role pins are unavailable here.
	const displayRoleColor = departmentId && departmentId !== chosenDepartment.department_id ? automaticRoleColor : personalRoleColor;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const departmentJobs = useJobs();
	const jobColor = useJobColors();
	const effectiveGroupBy: "role" | "job" = jobManagementEnabled ? (groupBy ?? "job") : "role";
	const { data, isError, isLoading, error } = useGetManagementCenterShiftPickUps({
		sinceDate: dateFrom,
		endDate: dateTo,
		roles,
		jobs,
		groupBy: jobManagementEnabled ? effectiveGroupBy : undefined,
		shiftHours,
		excludeHolidays,
		excludeAgency,
		departmentId,
	});

	useEffect(() => {
		if (isError) {
			trackError("fetch_management_center_shift_pick_ups_error", {
				error_message: error?.message,
			});
		}
	}, [isError, error, trackError]);

	// INH-3407: when grouping by job, the keys returned from the backend are job display names —
	// build a name→color lookup keyed by display name (matching what the BE emits).
	const jobNameToColor = useMemo(() => {
		const out = new Map<string, string>();
		for (const j of departmentJobs) out.set(j.displayName, jobColor(j.id).dot);
		return out;
	}, [departmentJobs, jobColor]);

	const groupKeyMode = jobManagementEnabled ? effectiveGroupBy : "role";
	const nameToColor = useMemo(() => {
		if (groupKeyMode === "job") {
			return (name: string) => jobNameToColor.get(name);
		}
		return (name: string) => stringToFullRoleToColor(name, displayRoleColor);
	}, [groupKeyMode, jobNameToColor, displayRoleColor]);
	const nameTransformer = useMemo(() => {
		if (groupKeyMode === "job") {
			return (name: string) => (name === UNKNOWN_KEY ? t("unknown") : name);
		}
		return (fullRole: string) => (fullRole === UNKNOWN_KEY ? t("unknown") : getShortRole(fullRole as NurseFullRole) || fullRole);
	}, [groupKeyMode, t]);

	const transformedData = useMemo(() => {
		if (isLoading || !data) return undefined;
		const end = dateTo ?? DateTime.now().setZone(timezone);
		const clampRange = buildClampRange(dateFrom, end, timezone);
		const groups = fetchedDataToBarDataGroups({ data, timezone, nameToColor, nameTransformer, clampRange });
		const mode = detectGroupingMode(data);
		const allPeriods = generateAllPeriodDateTimes({ dateFrom, dateTo: end, mode, timezone, weekStartDay: chosenDepartment.week_start_day });

		for (const group of groups) {
			for (const stack of group) {
				// This chart is time-mode, so every item's `dateOrLabel` is a DateTime.
				const existingKeys = new Set(stack.data.map((d) => (d.dateOrLabel as DateTime).toMillis()));
				for (const { dateTime } of allPeriods) {
					// Clamp gap-fill periods to match the (clamped) bar data so an edge week isn't double-plotted.
					const clamped = clampDateToRange(dateTime, clampRange);
					if (!existingKeys.has(clamped.toMillis())) {
						// Fill in missing dates with value 0, if no pickups.
						stack.data.push({ dateOrLabel: clamped, value: 0 });
					}
				}
				stack.data.sort((a, b) => (a.dateOrLabel as DateTime).toMillis() - (b.dateOrLabel as DateTime).toMillis());
			}
		}
		return groups;
	}, [isLoading, data, timezone, dateFrom, dateTo, nameToColor, nameTransformer, chosenDepartment.week_start_day]);

	const groupBySwitch =
		jobManagementEnabled && onGroupByChange ? (
			<SystemFlatSwitch
				data-testid="shift-pickups-group-by-switch"
				options={[
					{ label: t("role"), value: "role" as const, "data-testid": "shift-pickups-group-by-switch-role" },
					{ label: t("job"), value: "job" as const, "data-testid": "shift-pickups-group-by-switch-job" },
				]}
				activeIndexOrValue={effectiveGroupBy}
				onSwitch={(option) => onGroupByChange(option.value)}
			/>
		) : null;

	const headerTrailing =
		groupBySwitch || onShowAgencyPickupsToggle || trailing ? (
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				{groupBySwitch}
				{onShowAgencyPickupsToggle && (
					<FormControlLabel
						control={<Checkbox checked={showAgencyPickups ?? false} onChange={(_, checked) => onShowAgencyPickupsToggle(checked)} size="small" />}
						label={t("show.agency.pickups")}
						sx={{
							m: 0,
							"& .MuiFormControlLabel-label": {
								fontSize: { xl: "14px", xs: "10px" },
								color: INK.SECONDARY,
							},
						}}
					/>
				)}
				{trailing}
			</Box>
		) : undefined;

	return (
		<CommonDashboardBox sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 1, width: "calc(33% - 4px)", ...containerSx }}>
			<CommonDashboardHeader label={"Shift Pickups"} trailing={headerTrailing} />
			<InHouseBarChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				stackedGroups={transformedData}
				width={"100%"}
				height={containerSx ? "100%" : undefined}
			/>
		</CommonDashboardBox>
	);
};
